import React from 'react';
import { useTranslation } from 'react-i18next';

import Input from '../../../../../../../../common/data-entry/Input';
import Radio from '../../../../../../../../common/data-entry/InputSelectors/Radio';
import Select from '../../../../../../../../common/data-entry/Select';
import Toggle from '../../../../../../../../common/data-entry/Toggle';
import { i18nt } from '../../../../../../../../root';
import { resolvePostalCodeLabel, resolveRegionLabel } from '../../../../../../../../services/geo/resolvers.js';
import useGeo from '../../../../../../../../services/geo/useGeo';
import { NS_COMMON, NSC_ADDRESS, NSC_PATIENT, NSPP_PATIENT } from '../../../../../../../../services/i18n/namespaces';
import { caseInsensitiveStringIncludes } from '../../../../../../../../services/string';

import PhoneNumberInput from './PhoneNumberInput/PhoneNumberInput';

const dropDownOptions = [
  { id: 'YES', name: 'Yes' },
  { id: 'NO', name: 'No' },
  { id: 'NOT_ASKED', name: 'Not Asked' }
];

export default function PatientContactDetails({
  handlePhoneNumberChange,
  handleCountryChange,
  handleStateChange,
  handleInputChange,
  handlePreferredContactMethodChange,
  isRequiredField,
  getValidationMessage,
  address,
  info,
  fieldsDataDictionary,
  handleChangeSmsOptInDropdown,
  handleChangeInterestedInFutureResearch
}) {
  const { t } = useTranslation();
  const countryId = address.country;
  const regionId = address.state;
  const { countries, regions, matchedCountry, matchedRegion } = useGeo(countryId, regionId);

  const { phonesInfo, email, interestedInFutureResearch, smsOptIn } = info;

  return (
    <div className="row">
      <div className="col-12 apm-form-label">{t('patientContactDetails', { ns: NSPP_PATIENT })}</div>
      <div className="col-12 d-flex">
        <div className="col-4">
          <Toggle name="phonesInfo.neverCallAgain" checked={phonesInfo.neverCallAgain} onChange={handleInputChange}>
            {t('addOrEdit.neverCallAgain', { ns: NSPP_PATIENT })}
          </Toggle>
        </div>

        <div className="col-4">
          <Toggle
            name="phonesInfo.voicemailNotAllowed"
            checked={phonesInfo.voicemailNotAllowed}
            onChange={handleInputChange}
          >
            {t('voicemailNotAllowed', { ns: NSPP_PATIENT })}
          </Toggle>
        </div>
      </div>
      <div className="col-4">
        <Select
          name="smsOptIn"
          label={t('addOrEdit.smsOptIn', { ns: NSPP_PATIENT })}
          dataSource={dropDownOptions}
          value={findDropDownOption(smsOptIn) || null}
          onChange={handleChangeSmsOptInDropdown}
          clearable={false}
          customOptionTemplateFunction={yesNoCustomOptionTemplateFunction}
          customSelectedValueTemplateFunction={yesNoCustomSelectedValueTemplateFunction}
        />
      </div>
      <div className="col-4">
        <Select
          name="interestedInFutureResearch"
          label={t('interestedInFutureResearch', { ns: NSPP_PATIENT })}
          dataSource={dropDownOptions}
          value={findDropDownOption(interestedInFutureResearch) || null}
          onChange={handleChangeInterestedInFutureResearch}
          clearable={false}
          customOptionTemplateFunction={yesNoCustomOptionTemplateFunction}
          customSelectedValueTemplateFunction={yesNoCustomSelectedValueTemplateFunction}
        />
      </div>
      <div className="col-12" />
      <div className="col-4">
        <PhoneNumberInput
          label={t('addOrEdit.phonePrimary', { ns: NSPP_PATIENT })}
          name="phonesInfo.primary"
          value={phonesInfo.primary}
          onChange={handlePhoneNumberChange}
          validationMessage={getValidationMessage('phonesInfo.primary')}
          required={isRequiredField('phonesInfo.primary')}
        />
        <Radio.Group
          name="phonesInfo.primary.phoneType"
          checkedValue={phonesInfo.primary.phoneType}
          options={fieldsDataDictionary?.phoneTypeCodes?.map(type => ({
            ...type,
            name: t(`phoneTypeMap.${type.id}`, { ns: NSC_PATIENT })
          }))}
          onChange={function({ target }) {
            return handlePhoneNumberChange({ name: 'phonesInfo.primary', phoneType: target.value });
          }}
          optionLabelKey="name"
          optionValueKey="id"
        />
      </div>
      <div className="col-4">
        <PhoneNumberInput
          label={t('addOrEdit.phoneAlternate', { ns: NSPP_PATIENT })}
          name="phonesInfo.alternative"
          value={phonesInfo.alternative}
          onChange={handlePhoneNumberChange}
          validationMessage={getValidationMessage('phonesInfo.alternative')}
        />
        <Radio.Group
          name="phonesInfo.alternative.phoneType"
          checkedValue={phonesInfo.alternative.phoneType}
          options={fieldsDataDictionary?.phoneTypeCodes?.map(type => ({
            ...type,
            name: t(`phoneTypeMap.${type.id}`, { ns: NSC_PATIENT })
          }))}
          onChange={function({ target }) {
            return handlePhoneNumberChange({ name: 'phonesInfo.alternative', phoneType: target.value });
          }}
          optionLabelKey="name"
          optionValueKey="id"
        />
      </div>
      <div className="col-4">
        <Input
          label={t('email', { ns: NS_COMMON })}
          name="email"
          value={email}
          onChange={handleInputChange}
          validationMessage={getValidationMessage('email')}
          required={isRequiredField('email')}
        />
      </div>
      <div className="col-4 mt-2">
        <Select
          label={t('preferredContactMethod', { ns: NSPP_PATIENT })}
          name="preferredContactMethod"
          value={fieldsDataDictionary?.contactCodes?.find(({ id }) => id === phonesInfo.preferredContactMethod) || null}
          onChange={handlePreferredContactMethodChange}
          deselectOnSelectedOptionClick={false}
          dataSource={fieldsDataDictionary?.contactCodes}
          customOptionTemplateFunction={contactMethodCustomOptionTemplateFunction}
          customSelectedValueTemplateFunction={contactMethodCustomSelectedValueTemplateFunction}
        />
      </div>
      <div className="col-9"></div>
      <div className="col-4">
        <Select
          label={t('country', { ns: NSC_ADDRESS })}
          name="address.country"
          clearSearchOnSelection={true}
          searchable
          value={matchedCountry}
          onChange={handleCountryChange}
          required={isRequiredField('address.country')}
          validationMessage={getValidationMessage('address.country')}
          clearable={false}
          deselectOnSelectedOptionClick={false}
          dataSource={countries}
          customFilterFunction={countryCustomFilterFunction}
        />
      </div>
      <div className="col-4">
        <Input
          label={t('address1', { ns: NSC_ADDRESS })}
          name="address.addressLine1"
          value={address.addressLine1}
          onChange={handleInputChange}
          required={isRequiredField('address.addressLine1')}
          validationMessage={getValidationMessage('address.addressLine1')}
        />
      </div>
      <div className="col-4">
        <Input
          label={t('address2', { ns: NSC_ADDRESS })}
          name="address.addressLine2"
          value={address.addressLine2}
          onChange={handleInputChange}
        />
      </div>
      <div className="col-4">
        <Input
          label={t('city', { ns: NSC_ADDRESS })}
          name="address.city"
          value={address.city}
          onChange={handleInputChange}
          required={isRequiredField('address.city')}
          validationMessage={getValidationMessage('address.city')}
        />
      </div>
      <div className="col-4">
        <Select
          label={t(`${resolveRegionLabel(countryId)}`, { ns: NSC_ADDRESS })}
          clearSearchOnSelection={false}
          searchable
          clearable={false}
          closeOnSelectedOptionClick={false}
          deselectOnSelectedOptionClick={false}
          onChange={v => handleStateChange(v, 'address.state')}
          optionLabelKey="id"
          dataSource={regions}
          value={matchedRegion}
          required={isRequiredField('address.state')}
          validationMessage={getValidationMessage('address.state')}
          customSelectedValueTemplateFunction={selectedCountries => countryLabel(selectedCountries[0])}
          customOptionTemplateFunction={countryLabel}
          customFilterFunction={regionCustomFilterFunction}
        />
      </div>
      <div className="col-4">
        <Input
          label={t(`addOrEdit.${resolvePostalCodeLabel(countryId)}`, { ns: NSPP_PATIENT })}
          name="address.zipCode"
          value={address.zipCode}
          onChange={handleInputChange}
          required={isRequiredField('address.zipCode')}
          validationMessage={getValidationMessage('address.zipCode')}
        />
      </div>
    </div>
  );
  function findDropDownOption(optionId) {
    return dropDownOptions.find(({ id }) => id === optionId);
  }
}

export function countryLabel({ localName, name, id } = {}) {
  return (
    <React.Fragment>
      {id} <span className="country-description">({localName || name})</span>
    </React.Fragment>
  );
}

export function countryCustomFilterFunction(country, index, collection, searchString) {
  return caseInsensitiveStringIncludes(`${country.id} ${country.name}`, searchString);
}

export function regionCustomFilterFunction(region, index, collection, searchString) {
  return caseInsensitiveStringIncludes(`${region.id} ${region.name} ${region.localName || ''}`, searchString);
}

function contactMethodCustomOptionTemplateFunction(option) {
  return i18nt(`contactMethodMap.${option.id}`, { ns: NSC_PATIENT });
}

function contactMethodCustomSelectedValueTemplateFunction(options) {
  if (!options[0]) {
    return null;
  }
  return contactMethodCustomOptionTemplateFunction(options[0]);
}

function yesNoCustomOptionTemplateFunction(option) {
  return i18nt(`answerMap.${option.id}`);
}

function yesNoCustomSelectedValueTemplateFunction(options) {
  if (!options[0]) {
    return null;
  }
  return yesNoCustomOptionTemplateFunction(options[0]);
}
